import { jsPDF } from 'jspdf';
import { CONTENT } from '../data/content/index';
import { ROUTE_META } from '../data/routeMeta';
import { getSectionScores, getLevel, getOverallLevel } from './scoring';
import { getRoute } from './routing';

// ── Shared result-tier metadata ─────────────────────────────────────────────
// Lives here (rather than in ResultsScreen) because buildPDF and the on-screen
// results page need the same tier colors and copy. One source of truth.

export const TIER = {
  strong:     { bar: '#2AC0D0', text: '#2AC0D0', fill: 'rgba(42,192,208,0.08)',  label: 'Strong' },
  moderate:   { bar: '#2563FF', text: '#6B91FF', fill: 'rgba(37,99,255,0.08)',   label: 'Moderate' },
  developing: { bar: '#5B5FA8', text: '#9498D4', fill: 'rgba(91,95,168,0.08)',  label: 'Developing' },
};

// Per-dimension semantic colors — HIGH pain = red (problem),
// HIGH familiarity/clarity/readiness = teal (good).
export const DIM_SEMANTICS = {
  pain: {
    developing: { bar: '#2AC0D0', text: '#2AC0D0', fill: 'rgba(42,192,208,0.08)',  label: 'Low Pain' },
    moderate:   { bar: '#FBBF24', text: '#FBBF24', fill: 'rgba(251,191,36,0.08)',  label: 'Moderate Pain' },
    strong:     { bar: '#F87171', text: '#F87171', fill: 'rgba(248,113,113,0.10)', label: 'High Pain' },
  },
  familiarity: {
    developing: { bar: '#F87171', text: '#F87171', fill: 'rgba(248,113,113,0.10)', label: 'New to AI' },
    moderate:   { bar: '#FBBF24', text: '#FBBF24', fill: 'rgba(251,191,36,0.08)',  label: 'Growing' },
    strong:     { bar: '#2AC0D0', text: '#2AC0D0', fill: 'rgba(42,192,208,0.08)',  label: 'Fluent' },
  },
  clarity: {
    developing: { bar: '#F87171', text: '#F87171', fill: 'rgba(248,113,113,0.10)', label: 'Needs Mapping' },
    moderate:   { bar: '#FBBF24', text: '#FBBF24', fill: 'rgba(251,191,36,0.08)',  label: 'Partial Clarity' },
    strong:     { bar: '#2AC0D0', text: '#2AC0D0', fill: 'rgba(42,192,208,0.08)',  label: 'Clear Path' },
  },
  readiness: {
    developing: { bar: '#FBBF24', text: '#FBBF24', fill: 'rgba(251,191,36,0.08)',  label: 'Exploring' },
    moderate:   { bar: '#6B91FF', text: '#6B91FF', fill: 'rgba(107,145,255,0.08)', label: 'Almost Ready' },
    strong:     { bar: '#2AC0D0', text: '#2AC0D0', fill: 'rgba(42,192,208,0.08)',  label: 'Ready to Act' },
  },
};

export const DIM_CONFIG = [
  { key: 'pain',        label: 'The Pain',               shortLabel: 'Pain',        max: 12 },
  { key: 'familiarity', label: 'AI Familiarity',          shortLabel: 'Familiarity', max: 12 },
  { key: 'clarity',     label: 'Clarity of Opportunity',  shortLabel: 'Clarity',     max: 12 },
  { key: 'readiness',   label: 'Ready to Act',            shortLabel: 'Readiness',   max: 8  },
];

export const HERO_COPY = {
  strong: {
    headline: name => `${name}, you're ahead of most organizations.`,
    subtitle: n =>
      `You scored Strong on ${n} of four dimensions. The gap between where you are and your next level of leverage is small, and specific.`,
  },
  moderate: {
    headline: name => `${name}, you're building real momentum.`,
    subtitle: () =>
      "You have genuine strengths and clear areas to develop. There's meaningful leverage available when you know where to focus.",
  },
  developing: {
    headline: name => `${name}, here's where to focus first.`,
    subtitle: () =>
      "You're earlier in the journey than most organizations we work with. This report shows you exactly where to start.",
  },
};

// ── PDF palette & geometry ──────────────────────────────────────────────────

const PDF = {
  bg:       [18, 22, 36],
  surface:  [26, 31, 49],
  border:   [42, 48, 74],
  teal:     [42, 192, 208],
  accent:   [107, 145, 255],
  textPri:  [240, 242, 248],
  textSec:  [185, 189, 204],
  textMut:  [90, 98, 130],
  white:    [255, 255, 255],
};

const PAGE_W = 210;
const PAGE_H = 297;

// Rasterise an SVG from /public onto a canvas and return a PNG data-URL.
// Fetches as text and injects explicit width/height so the browser renders
// at the correct size (SVGs with only viewBox default to 300×150 on canvas).
// Uses a Blob URL to avoid canvas security restrictions.
// bgRgb pre-fills the canvas so jsPDF never sees transparent pixels.
async function loadLogo(svgPath, pxW, pxH, bgRgb) {
  try {
    const res = await fetch(svgPath);
    if (!res.ok) return null;
    const svg = (await res.text()).replace('<svg ', `<svg width="${pxW}" height="${pxH}" `);
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    return new Promise(resolve => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width  = pxW;
          canvas.height = pxH;
          const ctx = canvas.getContext('2d');
          if (bgRgb) {
            ctx.fillStyle = `rgb(${bgRgb.join(',')})`;
            ctx.fillRect(0, 0, pxW, pxH);
          }
          ctx.drawImage(img, 0, 0, pxW, pxH);
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  } catch {
    return null;
  }
}

function hexToRgb(hex) {
  if (!hex || !hex.startsWith('#')) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

function buildPDF({ gateData, scores, overallLevel, overallPct, content, route, displayRoute, meta, logoCover, logoHeader }) {
  // compress: true keeps the base64 payload well under the server's 8MB cap
  // and the client's postJson limit, even with the embedded raster logos.
  const doc  = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  let y = 0;

  const M  = 20;
  const CW = PAGE_W - M * 2;
  const BOTTOM = PAGE_H - 18;

  const SP = { xs: 2, sm: 4, md: 8, lg: 12, xl: 16 };

  const lh = pt => pt * 0.5;

  function fillBg() {
    doc.setFillColor(...PDF.bg);
    doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
  }

  function pageHeader() {
    doc.setFillColor(...PDF.teal);
    doc.rect(0, 0, PAGE_W, 0.8, 'F');
    const HLW = 24, HLH = 4.42;
    if (logoHeader) {
      doc.addImage(logoHeader, 'PNG', M, 5, HLW, HLH);
    } else {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...PDF.teal);
      doc.text('AEVARA', M, 9);
    }
    const textY = 5 + HLH / 2 + 1.3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF.textMut);
    doc.text('·  AI Readiness Report', M + HLW + 3, textY);
    doc.text(`${gateData.firstName}  ·  ${date}`, M + CW, textY, { align: 'right' });
    doc.setDrawColor(...PDF.border);
    doc.setLineWidth(0.2);
    doc.line(M, 14, M + CW, 14);
    y = 22;
  }

  function newPage() {
    doc.addPage();
    fillBg();
    pageHeader();
  }

  function ensureSpace(needed) {
    if (y + needed > BOTTOM) { newPage(); return true; }
    return false;
  }

  function rule(color = PDF.border, weight = 0.2) {
    doc.setDrawColor(...color);
    doc.setLineWidth(weight);
    doc.line(M, y, M + CW, y);
    y += SP.sm;
  }

  function kicker(text, color = PDF.teal) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...color);
    doc.text(text.toUpperCase(), M, y);
    y += SP.md;
  }

  function txt(text, pt, color, style = 'normal', maxW = CW, x = M) {
    doc.setFont('helvetica', style);
    doc.setFontSize(pt);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxW);
    doc.text(lines, x, y);
    return lines.length;
  }

  function pill(text, fillRgb, textRgb, px, py, w = 32) {
    const h = 5.5;
    doc.setFillColor(...fillRgb);
    doc.roundedRect(px, py - 4, w, h, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...textRgb);
    doc.text(text, px + w / 2, py - 0.5, { align: 'center' });
  }

  function bar(px, py, w, h, pct, fillRgb) {
    doc.setFillColor(...PDF.border);
    doc.roundedRect(px, py, w, h, h / 2, h / 2, 'F');
    if (pct > 0) {
      doc.setFillColor(...fillRgb);
      const filled = Math.max(w * (pct / 100), h);
      doc.roundedRect(px, py, filled, h, h / 2, h / 2, 'F');
    }
  }

  const overallTier = TIER[overallLevel];
  const tierRgb     = hexToRgb(overallTier.bar) || PDF.teal;
  const strongCount = DIM_CONFIG.filter(d => getLevel(scores[d.key], d.max) === 'strong').length;

  // ── PAGE 1: Cover ──────────────────────────────────────────────────────────
  fillBg();

  doc.setFillColor(...PDF.teal);
  doc.rect(0, 0, PAGE_W, 1.5, 'F');

  const LOGO_W = 36, LOGO_H = 6.62;
  y = 10;
  if (logoCover) {
    doc.addImage(logoCover, 'PNG', M, y, LOGO_W, LOGO_H);
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...PDF.teal);
    doc.text('AEVARA', M, y + 5);
  }
  const coverTextY = y + LOGO_H / 2 + 1.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...PDF.textMut);
  doc.text('AI Readiness Report', M + LOGO_W + 4, coverTextY);
  doc.text(date, M + CW, coverTextY, { align: 'right' });
  y = y + LOGO_H + SP.sm;
  rule();
  y += SP.md;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...PDF.textPri);
  const hlLines = doc.splitTextToSize(HERO_COPY[overallLevel].headline(gateData.firstName), CW * 0.75);
  doc.text(hlLines, M, y);
  y += hlLines.length * lh(22) + SP.sm;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...PDF.textSec);
  const subLines = doc.splitTextToSize(HERO_COPY[overallLevel].subtitle(strongCount), CW);
  doc.text(subLines, M, y);
  y += subLines.length * lh(9.5) + SP.md;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...PDF.textPri);
  doc.text(`${scores.total} / 44`, M, y);
  const scoreInlineW = doc.getTextWidth(`${scores.total} / 44`);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(22);
  doc.setTextColor(...PDF.textMut);
  doc.text('  points scored', M + scoreInlineW, y);
  y += lh(22) + SP.sm;

  pill(`${overallTier.label} overall`, tierRgb.map(c => Math.round(c * 0.2 + PDF.surface[0] * 0.8)), tierRgb, M, y + 4, 40);
  y += SP.lg;
  rule();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PDF.textMut);
  doc.text('OVERALL READINESS', M, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...tierRgb);
  doc.text(`${overallPct}%`, M + CW, y, { align: 'right' });
  y += SP.sm;
  bar(M, y, CW, 3.5, overallPct, tierRgb);
  y += SP.lg;
  rule();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...PDF.textMut);
  doc.text('DIMENSION SCORES', M, y);
  y += SP.md;

  const COL = CW / 4;
  DIM_CONFIG.forEach((dim, i) => {
    const sc   = scores[dim.key];
    const lv   = getLevel(sc, dim.max);
    const tr   = DIM_SEMANTICS[dim.key][lv];
    const pct  = Math.round((sc / dim.max) * 100);
    const rgb  = hexToRgb(tr.bar) || PDF.teal;
    const cx   = M + i * COL;

    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(dim.shortLabel, cx, y);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...rgb);
    doc.text(`${pct}%`, cx, y + 6.5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(`${sc} / ${dim.max} pts`, cx, y + 12);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...rgb);
    doc.text(tr.label, cx, y + 16.5);
    bar(cx, y + 19, COL - 8, 2.5, pct, rgb);
  });
  y += 26;
  rule();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...PDF.textMut);
  doc.text(`Prepared for: ${gateData.firstName}  ·  ${gateData.orgType}  ·  aevara.ai`, M, y);

  // ── PAGE 2: Score Breakdown ────────────────────────────────────────────────
  newPage();
  kicker('Score Breakdown');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PDF.textSec);
  doc.text('How you scored across each of the four AI readiness dimensions.', M, y);
  y += SP.lg;

  DIM_CONFIG.forEach(dim => {
    const sc    = scores[dim.key];
    const lv    = getLevel(sc, dim.max);
    const tr    = DIM_SEMANTICS[dim.key][lv];
    const block = content.dimensions[dim.key][lv];
    const pct   = Math.round((sc / dim.max) * 100);
    const rgb   = hexToRgb(tr.bar) || PDF.teal;
    const CARD_H = 34;

    ensureSpace(CARD_H + SP.sm);

    doc.setFillColor(...PDF.surface);
    doc.setDrawColor(...PDF.border);
    doc.setLineWidth(0.2);
    doc.roundedRect(M, y, CW, CARD_H, 2.5, 2.5, 'FD');
    doc.setFillColor(...rgb);
    doc.roundedRect(M, y, 3, CARD_H, 1.5, 1.5, 'F');

    const IX = M + 10;
    const IW = CW - 10;
    const RX = M + CW - 5;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...PDF.textPri);
    doc.text(dim.label, IX, y + 9);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...rgb);
    doc.text(`${pct}%`, RX, y + 9, { align: 'right' });

    doc.setFontSize(7);
    const pillW = doc.getTextWidth(tr.label) + 8;
    pill(tr.label, rgb.map(c => Math.round(c * 0.2 + PDF.surface[0] * 0.8)), rgb, IX, y + 15, pillW);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(`${sc} / ${dim.max} pts`, RX, y + 15, { align: 'right' });

    bar(IX, y + 19, IW - 5, 2.5, pct, rgb);

    const hlArr = doc.splitTextToSize(block.headline, IW - 5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...PDF.textSec);
    doc.text(hlArr[0] || '', IX, y + 28);

    y += CARD_H + SP.sm;
  });

  // ── PAGES 3+: Dimension Analyses ──────────────────────────────────────────
  newPage();
  kicker('Readiness Analysis');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PDF.textSec);
  doc.text('A full breakdown of what your scores mean and what to do about them.', M, y);
  y += SP.lg;

  DIM_CONFIG.forEach((dim, idx) => {
    const sc    = scores[dim.key];
    const lv    = getLevel(sc, dim.max);
    const block = content.dimensions[dim.key][lv];
    const tr    = DIM_SEMANTICS[dim.key][lv];
    const rgb   = hexToRgb(tr.bar) || PDF.teal;
    const pct   = Math.round((sc / dim.max) * 100);

    ensureSpace(SP.xl + lh(12));

    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...rgb);
    doc.text(dim.label.toUpperCase(), M, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(`${sc} / ${dim.max}  ·  ${pct}%`, M + CW, y, { align: 'right' });
    y += SP.sm;

    pill(tr.label, rgb.map(c => Math.round(c * 0.2 + PDF.surface[0] * 0.8)), rgb, M, y + 4, 30);
    y += SP.md;
    bar(M, y, CW, 2.5, pct, rgb);
    y += SP.md;

    ensureSpace(lh(12) + SP.sm);
    const hn = txt(block.headline, 12, PDF.textPri, 'bold');
    y += hn * lh(12) + SP.sm;

    block.body.split('\n\n').forEach(para => {
      ensureSpace(lh(9) * 3 + SP.sm);
      const pn = txt(para, 9, PDF.textSec);
      y += pn * lh(9) + SP.sm;
    });

    if (idx < DIM_CONFIG.length - 1) {
      y += SP.sm;
      ensureSpace(SP.xl);
      rule(PDF.border, 0.3);
      y += SP.sm;
    }
  });

  y += SP.sm;
  rule(PDF.teal, 0.4);

  ensureSpace(SP.xl + lh(13));
  kicker('What You Might Not Expect');

  const ihn = txt(content.insight.headline, 13, PDF.teal, 'bold');
  y += ihn * lh(13) + SP.md;

  content.insight.body.split('\n\n').forEach(para => {
    ensureSpace(lh(9) * 3 + SP.sm);
    const pn = txt(para, 9, PDF.textSec);
    y += pn * lh(9) + SP.sm;
  });

  y += SP.sm;
  rule(PDF.teal, 0.4);

  ensureSpace(SP.xl + lh(10));
  kicker('Three Things to Do This Week');

  const steps = content.actionableSteps[route] || content.actionableSteps.discovery;
  steps.forEach((step, i) => {
    ensureSpace(SP.xl + lh(10));

    doc.setFillColor(...PDF.surface);
    doc.roundedRect(M, y - 4, 8, 8, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...PDF.teal);
    doc.text(`${i + 1}`, M + 4, y, { align: 'center' });

    const shn = txt(step.title, 10, PDF.textPri, 'bold', CW - 12, M + 12);
    y += shn * lh(10) + SP.xs;

    const sbn = txt(step.body, 9, PDF.textSec, 'normal', CW - 12, M + 12);
    y += sbn * lh(9) + SP.md;
  });

  rule(PDF.border, 0.3);

  ensureSpace(SP.xl + lh(9));
  kicker('For Context');

  const statColW = (CW - SP.md) / 2;
  const statStartY = y;

  content.stats.headlines.slice(0, 2).forEach((stat, i) => {
    const sx = i === 0 ? M : M + statColW + SP.md;
    let sy = statStartY;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(...PDF.teal);
    doc.text(stat.figure, sx, sy);
    sy += lh(20) + SP.xs;

    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...PDF.textSec);
    const lb = doc.splitTextToSize(stat.label, statColW);
    doc.text(lb, sx, sy);
    sy += lb.length * lh(9) + SP.xs;

    doc.setFont('helvetica', 'italic'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(stat.source, sx, sy);
  });

  y = statStartY + lh(20) + SP.xs + 2 * lh(9) + SP.xs + lh(7.5) + SP.lg;
  rule(PDF.border, 0.3);

  const whenReady = content.whenReady[displayRoute];
  ensureSpace(SP.xl + lh(13));
  kicker('Your Recommended Next Step');

  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...PDF.accent);
  doc.text(meta.label.toUpperCase(), M, y);
  y += SP.md;

  const chn = txt(whenReady.headline, 13, PDF.textPri, 'bold');
  y += chn * lh(13) + SP.md;

  whenReady.body.split('\n\n').forEach(para => {
    ensureSpace(lh(9) * 3 + SP.sm);
    const pn = txt(para, 9, PDF.textSec);
    y += pn * lh(9) + SP.sm;
  });

  const btnW = 72;
  const btnH = 9;
  const btnX = M;
  ensureSpace(SP.md + btnH + SP.sm);
  y += SP.md;
  doc.setFillColor(...PDF.teal);
  doc.roundedRect(btnX, y, btnW, btnH, 2, 2, 'F');
  const btnLabel = whenReady.ctaText || 'Schedule a Conversation';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PDF.bg);
  doc.text(btnLabel, btnX + btnW / 2, y + btnH / 2 + 1, { align: 'center' });
  doc.link(btnX, y, btnW, btnH, { url: meta.bookingUrl });
  y += btnH + SP.sm;

  ensureSpace(SP.lg);
  y += SP.sm;
  rule(PDF.teal, 0.4);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
  doc.text('aevara.ai', M, y);
  doc.text(`${gateData.firstName}  ·  AI Readiness Report  ·  ${date}`, M + CW, y, { align: 'right' });

  return doc;
}

// ── Public API ──────────────────────────────────────────────────────────────

// Builds the full report PDF from raw answers + gate data. Returns a jsPDF
// doc so callers can either save() it or hand its base64 to the server.
export async function generateReportDoc(answers, gateData) {
  const scores       = getSectionScores(answers);
  const route        = getRoute(scores);
  const overallLevel = getOverallLevel(scores.total);
  const overallPct   = Math.round((scores.total / 44) * 100);
  const content      = CONTENT[gateData.orgType] || CONTENT.smallBusiness;
  const displayRoute = gateData.orgType === 'government' ? 'discovery' : route;
  const meta         = ROUTE_META[displayRoute];

  const [logoCover, logoHeader] = await Promise.all([
    loadLogo('/Aevara-Header-Logo.svg', 2880, 529, PDF.bg),
    loadLogo('/Aevara-Header-Logo.svg', 1920, 353, PDF.bg),
  ]);

  return buildPDF({
    gateData, scores, overallLevel, overallPct,
    content, route, displayRoute, meta,
    logoCover, logoHeader,
  });
}

// Returns the PDF as a base64 string (no data-URI prefix) for the server's
// email endpoint. jsPDF emits `data:application/pdf;filename=...;base64,...`
// so take only the portion after the final comma.
export function reportBase64(doc) {
  const uri = doc.output('datauristring');
  return uri.slice(uri.lastIndexOf(',') + 1);
}

// Filename must stay byte-identical to the one the server attaches in Brevo,
// so the downloaded copy and the mailed copy are recognizably the same file.
// Server source of truth: artifacts/api-server/src/routes/report-email.ts.
export function downloadReport(doc, firstName) {
  doc.save(`aevara-ai-readiness-report-${firstName.toLowerCase().replace(/\s+/g, '-')}.pdf`);
}
